import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Check, CheckCircle } from "lucide-react";
import { ColorPicker } from "@/components/settings/ColorPicker";
import ImageUpload from "@/components/common/ImageUpload";
import { FuturisticLoader } from "@/components/ui/FuturisticLoader";

interface Option {
  value: string;
  label: string;
}

interface ResponseOptionsProps {
  type:
    | "buttons"
    | "select"
    | "multi-select"
    | "text"
    | "branding"
    | "ai-pitch"
    | "welcome-form"
    | "persona-cards";
  options?: Option[];
  onSelect: (
    value:
      | string
      | string[]
      | {
          website?: string;
          brand_color?: string;
          logo_file?: File | null;
          continue?: boolean;
          logo_preview_url?: string;
          textValue?: string;
          firstName?: string;
          lastName?: string;
          organizationName?: string;
          selectedPersona?: string | null;
        },
  ) => void;
  selectedValues?: string[];
  selectedValue?: string;
  placeholder?: string;
  className?: string;
  // New props for text and branding types
  textValue?: string;
  brandingData?: {
    brand_color?: string;
    logo_file?: File | null;
    logo_url?: string;
    logo_preview_url?: string;
  };
  // New props for ai-pitch type
  aiPitchReady?: boolean;
  isGeneratingPitch?: boolean;
  // New props for welcome-form type
  welcomeFormData?: {
    firstName: string;
    lastName: string;
    organizationName: string;
  };
  // New props for persona-cards type
  personaData?: {
    personas: Array<{
      id: string;
      name: string;
      title: string;
      description: string;
      avatar: string;
    }>;
    selectedPersona?: string | null;
  };
  // Submission state props for guards
  disabled?: boolean;
  isSubmitting?: boolean;
  submitText?: string;
}

export function ResponseOptions({
  type,
  options,
  onSelect,
  selectedValues = [],
  selectedValue,
  placeholder,
  className = "",
  textValue,
  brandingData,
  aiPitchReady,
  isGeneratingPitch,
  welcomeFormData,
  personaData,
  disabled,
  isSubmitting,
  submitText,
}: ResponseOptionsProps) {
  const [localTextValue, setLocalTextValue] = useState(textValue || "");

  const handleButtonSelect = (value: string) => {
    onSelect(value);
  };

  const handleMultiSelect = (value: string) => {
    const newSelection = selectedValues.includes(value)
      ? selectedValues.filter((v) => v !== value)
      : [...selectedValues, value];
    onSelect(newSelection);
  };

  const handleSingleSelect = (value: string) => {
    onSelect(value);
  };

  if (type === "buttons") {
    return (
      <div
        className={`flex justify-end animate-in slide-in-from-bottom-4 duration-500 ${className}`}
      >
        <div className="grid grid-cols-2 gap-3 max-w-md">
          {options?.map((option) => {
            const isSelected = selectedValue === option.value;
            return (
              <button
                key={option.value}
                onClick={() => handleButtonSelect(option.value)}
                className={`h-12 p-4 rounded-lg border-2 text-left transition-all duration-200 font-normal text-sm
                  ${
                    isSelected
                      ? "border-[#ecb22d] bg-[#ecb22d]/10 text-gray-900"
                      : "border-gray-200 bg-white hover:border-[#ecb22d] hover:bg-[#ecb22d]/10 text-gray-700"
                  }`}
              >
                {option.label}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  if (type === "select") {
    return (
      <div
        className={`flex justify-end animate-in slide-in-from-bottom-4 duration-500 ${className}`}
      >
        <div className="space-y-3 max-w-md w-full">
          {options?.map((option) => {
            const isSelected = selectedValue === option.value;
            const isLoadingOption = option.value === "loading";
            return (
              <button
                key={option.value}
                onClick={() =>
                  !isLoadingOption && handleSingleSelect(option.value)
                }
                disabled={isLoadingOption}
                className={`w-full p-4 rounded-lg border-2 text-left transition-all duration-200 font-normal
                  ${
                    isSelected
                      ? "border-[#ecb22d] bg-[#ecb22d]/10 text-gray-900"
                      : isLoadingOption
                        ? "border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed"
                        : "border-gray-200 bg-white hover:border-[#ecb22d] hover:bg-[#ecb22d]/10 text-gray-700"
                  }`}
              >
                {option.label}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  if (type === "multi-select") {
    return (
      <div
        className={`flex justify-end animate-in slide-in-from-bottom-4 duration-500 ${className}`}
      >
        <div className="space-y-3 max-w-md w-full">
          {options?.map((option) => {
            const isSelected = selectedValues.includes(option.value);
            return (
              <button
                key={option.value}
                onClick={() => handleMultiSelect(option.value)}
                className={`
                  w-full p-4 rounded-lg border-2 text-left transition-all duration-200 
                  flex items-center justify-between font-normal
                  ${
                    isSelected
                      ? "border-[#ecb22d] bg-[#ecb22d]/10 text-gray-900"
                      : "border-gray-200 bg-white hover:border-[#ecb22d] hover:bg-[#ecb22d]/10 text-gray-700"
                  }
                `}
              >
                <span>{option.label}</span>
                {isSelected && (
                  <div className="w-5 h-5 bg-[#ecb22d] rounded-full flex items-center justify-center">
                    <Check className="w-3 h-3 text-white" />
                  </div>
                )}
              </button>
            );
          })}
          {selectedValues.length > 0 && (
            <button
              className={`mt-4 w-full font-normal p-3 rounded-lg transition-all duration-200 ${
                disabled || isSubmitting
                  ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                  : "bg-[#ecb22d] hover:bg-[#d9a429] text-white"
              }`}
              onClick={() => onSelect(selectedValues)}
              disabled={disabled || isSubmitting}
            >
              {isSubmitting ? (
                <div className="flex items-center justify-center gap-2">
                  <FuturisticLoader size={20} />
                  <span>{submitText || "Submitting..."}</span>
                </div>
              ) : (
                "Continue"
              )}
            </button>
          )}
        </div>
      </div>
    );
  }

  if (type === "text") {
    return (
      <div
        className={`flex justify-end animate-in slide-in-from-bottom-4 duration-500 ${className}`}
      >
        <div className="space-y-3 max-w-md w-full">
          <Input
            type="text"
            value={localTextValue}
            onChange={(e) => {
              setLocalTextValue(e.target.value);
              onSelect(e.target.value);
            }}
            placeholder={placeholder}
            className="w-full p-4 rounded-lg border-2 text-left transition-all duration-200 font-normal"
          />
          <button
            className={`w-full font-normal p-3 rounded-lg transition-all duration-200 ${
              disabled || isSubmitting
                ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                : "bg-[#ecb22d] hover:bg-[#d9a429] text-white"
            }`}
            onClick={() =>
              onSelect({ continue: true, textValue: localTextValue })
            }
            disabled={disabled || isSubmitting}
          >
            {isSubmitting ? (
              <div className="flex items-center justify-center gap-2">
                <FuturisticLoader size={20} />
                <span>{submitText || "Submitting..."}</span>
              </div>
            ) : (
              "Continue"
            )}
          </button>
        </div>
      </div>
    );
  }

  if (type === "branding") {
    return (
      <div
        className={`flex justify-end animate-in slide-in-from-bottom-4 duration-500 ${className}`}
      >
        <div className="space-y-6 max-w-md w-full">
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">
              Brand Color
            </label>
            <ColorPicker
              color={brandingData?.brand_color || "#3b82f6"}
              onChange={(color) => onSelect({ brand_color: color })}
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">Logo</label>
            <ImageUpload
              onFileSelect={(file) => onSelect({ logo_file: file })}
              previewUrl={
                brandingData?.logo_preview_url || brandingData?.logo_url || null
              }
              maxSize={2}
              className="h-40 w-40 rounded-full overflow-hidden"
            />
          </div>
          <button
            className={`w-full font-normal p-3 rounded-lg transition-all duration-200 ${
              disabled || isSubmitting
                ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                : "bg-[#ecb22d] hover:bg-[#d9a429] text-white"
            }`}
            onClick={() => onSelect({ continue: true })}
            disabled={disabled || isSubmitting}
          >
            {isSubmitting ? (
              <div className="flex items-center justify-center gap-2">
                <FuturisticLoader size={20} />
                <span>{submitText || "Submitting..."}</span>
              </div>
            ) : (
              "Continue"
            )}
          </button>
        </div>
      </div>
    );
  }

  if (type === "ai-pitch") {
    const isDisabled = !aiPitchReady || isGeneratingPitch;
    const buttonText = isGeneratingPitch
      ? "Generating your plan..."
      : aiPitchReady
        ? "Let's go 🚀"
        : "Preparing...";

    return (
      <div
        className={`flex justify-center animate-in slide-in-from-bottom-4 duration-500 ${className}`}
      >
        <button
          className={`font-semibold px-8 py-4 rounded-lg transition-all duration-200 text-lg ${
            isDisabled
              ? "bg-gray-300 text-gray-500 cursor-not-allowed"
              : "bg-[#ecb22d] hover:bg-[#d9a429] text-white transform hover:scale-105"
          }`}
          onClick={() => onSelect("continue")}
          disabled={isDisabled}
        >
          {isGeneratingPitch && (
            <span className="inline-flex items-center mr-3">
              <FuturisticLoader size={20} />
            </span>
          )}
          {buttonText}
        </button>
      </div>
    );
  }

  if (type === "welcome-form") {
    return (
      <div
        className={`flex justify-end animate-in slide-in-from-bottom-4 duration-500 ${className}`}
      >
        <div className="space-y-6 max-w-md w-full">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">
                First Name
              </label>
              <Input
                type="text"
                value={welcomeFormData?.firstName || ""}
                onChange={(e) => onSelect({ firstName: e.target.value })}
                placeholder="First Name"
                className="w-full p-4 rounded-lg border-2 text-left transition-all duration-200 font-normal"
                required
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">
                Last Name
              </label>
              <Input
                type="text"
                value={welcomeFormData?.lastName || ""}
                onChange={(e) => onSelect({ lastName: e.target.value })}
                placeholder="Last Name"
                className="w-full p-4 rounded-lg border-2 text-left transition-all duration-200 font-normal"
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">
              Company Name
            </label>
            <Input
              type="text"
              value={welcomeFormData?.organizationName || ""}
              onChange={(e) => onSelect({ organizationName: e.target.value })}
              placeholder="Company Name"
              className="w-full p-4 rounded-lg border-2 text-left transition-all duration-200 font-normal"
              required
            />
          </div>

          <button
            className={`w-full font-normal p-3 rounded-lg transition-all duration-200 ${
              welcomeFormData?.firstName?.trim() &&
              welcomeFormData?.lastName?.trim() &&
              welcomeFormData?.organizationName?.trim() &&
              !disabled &&
              !isSubmitting
                ? "bg-[#ecb22d] hover:bg-[#d9a429] text-white"
                : "bg-gray-300 text-gray-500 cursor-not-allowed"
            }`}
            onClick={() => onSelect({ continue: true })}
            disabled={
              !(
                welcomeFormData?.firstName?.trim() &&
                welcomeFormData?.lastName?.trim() &&
                welcomeFormData?.organizationName?.trim()
              ) ||
              disabled ||
              isSubmitting
            }
          >
            {isSubmitting ? (
              <div className="flex items-center justify-center gap-2">
                <FuturisticLoader size={20} />
                <span>{submitText || "Submitting..."}</span>
              </div>
            ) : (
              "Continue"
            )}
          </button>
        </div>
      </div>
    );
  }

  if (type === "persona-cards") {
    return (
      <div
        className={`flex justify-end animate-in slide-in-from-bottom-4 duration-500 ${className}`}
      >
        <div className="space-y-4 max-w-2xl w-full">
          {personaData?.personas.map((persona) => {
            const isSelected = personaData.selectedPersona === persona.id;
            return (
              <div
                key={persona.id}
                className={`p-6 bg-white rounded-2xl border-2 cursor-pointer transition-all duration-300 hover:shadow-lg ${
                  isSelected
                    ? "border-[#ecb22d] shadow-lg"
                    : "border-gray-200 hover:border-gray-300"
                }`}
                onClick={() => onSelect(persona.id)}
              >
                <div className="flex items-center gap-6">
                  {/* Avatar Column */}
                  <div className="flex-shrink-0">
                    <img
                      src={persona.avatar}
                      alt={persona.name}
                      className="w-20 h-20 rounded-full border-4 border-white shadow-lg"
                    />
                  </div>

                  {/* Content Column */}
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-[#ecb22d] mb-1">
                      {persona.name}
                    </h3>
                    <p className="text-gray-900 font-normal">{persona.title}</p>
                  </div>

                  {/* Selection Indicator */}
                  {isSelected && (
                    <div className="flex-shrink-0">
                      <CheckCircle className="h-8 w-8 text-[#ecb22d]" />
                    </div>
                  )}
                </div>
              </div>
            );
          })}
          {/* No continue button needed - auto-advance on selection */}
        </div>
      </div>
    );
  }

  return null;
}
